import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import TextField from "@mui/material/TextField";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ReplayIcon from "@mui/icons-material/Replay";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import StorageIcon from "@mui/icons-material/Storage";
import ArticleIcon from "@mui/icons-material/Article";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { SnackBarStoreAction } from "Store/SnackBar";
import axiosInstance from "services/axiosInstance";
import { COPILOT_AGENT_CHAT, COPILOT_AGENT_FEEDBACK, COPILOT_AGENT_SUGGESTIONS } from "services/CONSTANTS";
import { renderMarkdown } from "./markdown";
import { downloadCsv, downloadTextFile } from "./csvExport";
import { loadConversation, saveConversation, clearConversation, subscribeToConversationUpdates } from "./conversationStorage";
import { normalizeMessages, resolveMergeTarget, appendAgentMessage, applyRegenerateVariant } from "./agentReplyMerge";
import styles from "./CopilotChatPanel.module.css";

// ASTT-7409: Copilot Studio integration. Structurally this is a near-copy of
// AgentChatPanel.js (the ADK/Cloud Run agent's panel) -- feedback, resize,
// conversation persistence, quick questions, CSV export, and markdown
// rendering are all identical, since none of that is specific to which
// backend answers the question. The one real difference is sendMessage:
// Copilot Studio has no token-level streaming via Direct Line, so this
// waits for the full answer in one request/response instead of parsing an
// SSE stream. Kept as a separate file (not a shared component with a
// backend-selector prop) so AgentChatPanel.js stays completely untouched
// while both agents are evaluated in parallel -- see CopilotChatFab.js's
// remarks for the same reasoning applied to the launcher.

const QUICK_QUESTIONS = {
  database: [
    "Look up Oracle ID 10293847",
    "What's the status of class roster 1210?",
  ],
  documents: [
    "How do I create a class request?",
    "How do I add trainees to a roster?",
  ],
};
// ^ Used as the initial state and fallback for the fetched, role-based
// suggestions below (see the suggestions-fetch effect) -- shown briefly on
// mount before the fetch resolves, and kept as-is if that fetch ever fails,
// so the empty state is never left blank.

const MIN_WIDTH = 360;
const MIN_HEIGHT = 420;
const MAX_WIDTH_MARGIN = 36;
const MAX_HEIGHT_MARGIN = 36;

// Feature: smarter "thinking" phases based on elapsed time. Direct Line has
// no streaming/progress signal for Copilot Studio agents to hook into (see
// sendMessage's remarks), so this is a plain timer-driven label progression
// instead -- not tied to any real signal from the agent, just a reasonable
// guess at what's likely happening based on how long a single-tool vs.
// multi-tool question tends to take (see ReplyPollTimeout's own remarks in
// DirectLineAgentService.cs for the real-world timings this is based on).
const THINKING_PHASES = [
  { afterMs: 0, label: "Thinking..." },
  { afterMs: 3000, label: "Looking that up..." },
  { afterMs: 10000, label: "Still working on it..." },
  { afterMs: 25000, label: "This is taking longer than usual..." },
];

function newSessionId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `sess-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Maps a failed sendMessage call to a specific, human-readable reason,
 * rather than one generic "failed to reach the agent" string for every
 * case. Reads the short status code our own backend returns (see
 * DirectLineAgentService.ChatAsync's ApiResponse.message values) when a
 * response came back at all -- a 504 (agent took too long) and a 502 (the
 * activity send itself was rejected) are genuinely different situations,
 * and telling them apart is more useful than a single catch-all.
 */
function getRetryReason(err) {
  if (err?.code === "ERR_CANCELED") return "Stopped.";
  const code = err?.response?.data?.message;
  if (code === "GatewayTimeout") return "The agent took longer than expected to respond.";
  if (code === "BadGateway") return "The agent couldn't process that question right now.";
  if (code === "ServiceUnavailable") return "The agent isn't available in this environment right now.";
  if (code === "InternalServerError") return "Something went wrong processing that question.";
  if (!err?.response) return "Couldn't reach the agent — please check your connection.";
  return "Something went wrong. Please try again.";
}

/** Formats a millisecond duration as a short, human-readable label -- "850ms"
 * under a second, "2.3s" style otherwise, matching how a duration figure
 * would naturally be read rather than showing raw milliseconds always. */
function formatDuration(ms) {
  if (ms == null) return null;
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/** Builds one agent-answer "variant" from a /chat response payload -- the
 * unit that both a fresh question and a Regenerate attempt produce, and
 * that renderVariantPager lets the user page between when a message has
 * more than one. */
function makeVariant(data) {
  return {
    text: data.answer ?? "I didn't get a response for that — please try again.",
    path: data.path ?? null,
    sources: data.sources ?? [],
    followups: data.followups ?? [],
    logId: data.logId ?? null,
    durationMs: data.durationMs ?? null,
    feedback: null,
  };
}

// normalizeMessages now lives in agentReplyMerge.js (see ASTT-7496) so both
// this component and that module's unit tests share one implementation.

// ASTT-7496 (Group 4 housekeeping): namespaces this panel's sessionStorage
// key separately from AgentChatPanel.js's (the ADK/Cloud Run agent's own
// panel) -- see conversationStorage.js's own remarks. Before this, both
// panels shared one sessionStorage slot, so whichever agent's panel a user
// had open last would silently overwrite the other's conversation if both
// were ever shown to the same user.
const STORAGE_NAMESPACE = "copilot";

export default function CopilotChatPanel({ onClose, agentStatus, onBeforeDownload }) {
  const dispatch = useDispatch();
  const isConnected = agentStatus === "online";

  const initialConversationRef = useRef(loadConversation(STORAGE_NAMESPACE));

  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [progressLabel, setProgressLabel] = useState(THINKING_PHASES[0].label);
  const [suggestions, setSuggestions] = useState(QUICK_QUESTIONS);
  const [messages, setMessages] = useState(() => {
    const initial = normalizeMessages(initialConversationRef.current?.messages ?? []);
    const last = initial[initial.length - 1];
    if (last && last.role === "user") {
      const fixed = [...initial];
      fixed[fixed.length - 1] = { ...last, retryReason: "Didn't get a reply before this session ended." };
      return fixed;
    }
    return initial;
  });
  // Three docking tiers: "default" | "max" | "fullscreen" -- all sized/
  // positioned via CSS that reads from --astt-sidebar-width/
  // --astt-header-height (see the docking-bounds effect below and
  // CopilotChatPanel.module.css), so none of the three can ever overlap
  // the sidebar or header regardless of screen resolution -- the actual
  // bug being fixed here, not just adding a third size for its own sake.
  const [sizeMode, setSizeMode] = useState("default");
  const [toastMessage, setToastMessage] = useState("");
  const [openCommentIdx, setOpenCommentIdx] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const sessionIdRef = useRef(initialConversationRef.current?.sessionId ?? newSessionId());
  // ASTT-7496 (B2): unique per MOUNT (not per session -- a session can span
  // multiple mounts via close/reopen), used only to tag this instance's own
  // writes so subscribeToConversationUpdates below can tell "a write I just
  // made myself" apart from "a write a DIFFERENT, now-unmounted instance of
  // this same panel made in the background."
  const instanceIdRef = useRef(newSessionId());
  const cancelRef = useRef(false);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const panelRef = useRef(null);
  const resizingRef = useRef(false);
  const toastTimerRef = useRef(null);
  // Refs (not state) for the drag-resize handler's own bounds check --
  // that handler is a plain DOM pointer-event listener set up once per
  // drag, so it needs the LATEST measured values without capturing a
  // stale closure over state from whenever the drag started.
  const sidebarWidthRef = useRef(0);
  const headerHeightRef = useRef(0);
  const location = useLocation();
  // Holds the in-flight request's AbortController so handleStop can cancel
  // it -- a NEW controller per sendMessage call, since a stale one from a
  // previous (already-finished) turn should never be able to abort a
  // LATER one.
  const abortControllerRef = useRef(null);

  // ===== Typewriter reveal (client-side simulation) =====
  // Direct Line has no token-level streaming for Copilot Studio agents (see
  // sendMessage's own remarks) -- the full answer always exists before
  // anything can be shown. This animates the ALREADY-COMPLETE answer
  // appearing progressively rather than all at once, purely as a display
  // effect layered on top of state that's already fully populated --
  // `messages`/sessionStorage always hold the real, complete text
  // immediately; only what's currently rendered on screen lags behind
  // while `revealState` catches up. Declared here (not down near
  // renderTurnRail) because the cleanup and scroll-follow effects above
  // need to reference revealIntervalRef/revealState before those are used.
  //
  // Only one message reveals at a time, tracked by a single object rather
  // than per-message state: idx identifies WHICH message, variantIndex
  // WHICH of its variants (so switching the variant pager to an
  // already-received older variant never re-triggers a reveal -- only a
  // genuinely new answer, from a fresh question or Regenerate, does).
  const revealIntervalRef = useRef(null);
  const [revealState, setRevealState] = useState(null);

  useEffect(() => {
    cancelRef.current = false;
    return () => {
      cancelRef.current = true;
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      if (revealIntervalRef.current) clearInterval(revealIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    saveConversation(STORAGE_NAMESPACE, sessionIdRef.current, messages, instanceIdRef.current);
  }, [messages]);

  // ASTT-7496 (B2): the counterpart to instanceIdRef -- reacts when a
  // DIFFERENT instance of this same panel (a previous mount, whose
  // in-flight request was still resolving when the panel was closed and
  // then reopened before that response arrived) writes a completed answer
  // to sessionStorage in the background. Without this, the newly-mounted
  // panel would show nothing for that turn until closed and reopened a
  // SECOND time. Only adopts the stored data if it's for the CURRENT
  // session and genuinely has more turns than what's already rendered --
  // the length check avoids fighting with this instance's own in-flight
  // requests, which already update state directly and don't need this path.
  useEffect(() => {
    return subscribeToConversationUpdates(STORAGE_NAMESPACE, instanceIdRef.current, (updatedSessionId) => {
      if (cancelRef.current) return;
      if (updatedSessionId !== sessionIdRef.current) return;
      const stored = loadConversation(STORAGE_NAMESPACE);
      if (!stored || stored.sessionId !== sessionIdRef.current) return;
      if (stored.messages.length <= messagesRef.current.length) return;
      setMessages(normalizeMessages(stored.messages));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kept in sync with `messages` via effect (not read directly from state)
  // so an async callback -- e.g. sendMessage's axios .then continuation --
  // can reliably learn the array's CURRENT length without depending on
  // `messages` itself (which would need to be threaded into sendMessage's
  // useCallback deps and would make the whole callback identity change on
  // every single message). Used by the typewriter reveal below to compute
  // the index a new agent message will land at, right before appending it.
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, revealState?.revealedCount]);

  // Measures the ACTUAL rendered Header/Sidebar (not a guessed fixed value --
  // the sidebar is percentage-width (App.css: .side-bar { width: 30% }) and
  // both it and the header are conditionally rendered per-route (see
  // Header.js's showHeader and Sidebar.js's showSidebar), so no static CSS
  // value would be reliable across pages). Sets the results as CSS custom
  // properties on the document root, which CopilotChatPanel.module.css reads
  // via var(--astt-sidebar-width)/var(--astt-header-height) to keep the
  // panel's default/max/fullscreen sizes all within the real docking area.
  // Re-measures on route change (Header/Sidebar's own show/hide effects can
  // lag one tick behind navigation, hence the short setTimeout) and on
  // window resize.
  useEffect(() => {
    const measure = () => {
      const header = document.querySelector(".home-section-container");
      const sidebar = document.querySelector(".side-bar");
      const headerHeight = header?.getBoundingClientRect().height ?? 0;
      const sidebarWidth = sidebar?.getBoundingClientRect().width ?? 0;
      headerHeightRef.current = headerHeight;
      sidebarWidthRef.current = sidebarWidth;
      document.documentElement.style.setProperty("--astt-header-height", `${headerHeight}px`);
      document.documentElement.style.setProperty("--astt-sidebar-width", `${sidebarWidth}px`);
    };

    measure();
    const settleTimer = setTimeout(measure, 150);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(settleTimer);
      window.removeEventListener("resize", measure);
    };
  }, [location.pathname]);

  // Feature: personalized starter questions instead of the fixed/hardcoded
  // set, based on the logged-in user's own role claims (see
  // CopilotAgentController.GetSuggestions). Fetched once on mount -- the
  // suggestions only matter for the EMPTY-conversation state, so there's no
  // need to refetch mid-conversation. Falls back to (and stays at) the
  // hardcoded QUICK_QUESTIONS default on any failure, silently -- this is a
  // nice-to-have personalization, not something worth surfacing an error
  // banner for if it doesn't load.
  useEffect(() => {
    let cancelled = false;
    const fetchSuggestions = async () => {
      try {
        const response = await axiosInstance.get(COPILOT_AGENT_SUGGESTIONS());
        const data = response?.data?.data;
        if (!cancelled && data?.database && data?.documents) {
          setSuggestions({ database: data.database, documents: data.documents });
        }
      } catch {
        // Silently keep the QUICK_QUESTIONS fallback already in state.
      }
    };
    fetchSuggestions();
    return () => {
      cancelled = true;
    };
  }, []);

  // Advances progressLabel through THINKING_PHASES purely on elapsed time --
  // starts fresh at "Thinking..." every time a new request begins, and
  // resets back to the first phase once loading ends (whether that's a
  // success or a failure) so the NEXT question starts from the beginning
  // rather than continuing wherever the previous one left off.
  useEffect(() => {
    if (!loading) {
      setProgressLabel(THINKING_PHASES[0].label);
      return undefined;
    }
    const timers = THINKING_PHASES.slice(1).map((phase) =>
      setTimeout(() => setProgressLabel(phase.label), phase.afterMs)
    );
    return () => timers.forEach(clearTimeout);
  }, [loading]);

  const showToast = useCallback((msg) => {
    setToastMessage(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastMessage(""), 1800);
  }, []);

  // Per-table copy/export, registered as window-level handlers since
  // markdown.js renders tables as raw HTML strings (via
  // dangerouslySetInnerHTML) with no React component boundary around each
  // individual table -- there's nowhere else to attach a real onClick.
  // Each table gets a unique data-md-table-id at render time (see
  // markdown.js's renderTableBlock); these handlers read the actual
  // rendered DOM at click time rather than tracking table data separately
  // in React state, so there's no risk of the button acting on stale data
  // after a re-render. Must come after showToast's own declaration above --
  // it's referenced in this effect's dependency array.
  useEffect(() => {
    const readTable = (tableId) => {
      const wrap = document.querySelector(`[data-md-table-id="${tableId}"]`);
      const table = wrap?.querySelector("table");
      if (!table) return null;
      const headers = Array.from(table.querySelectorAll("thead th")).map((c) => c.textContent);
      const rows = Array.from(table.querySelectorAll("tbody tr")).map((tr) =>
        Array.from(tr.querySelectorAll("td")).map((td) => td.textContent)
      );
      return { headers, rows };
    };

    window.__astt_copyMdTable = (tableId) => {
      const t = readTable(tableId);
      if (!t) return;
      // Tab-separated, not comma -- pastes directly into Excel/Sheets as
      // proper columns, which is the far more common destination for a
      // "copy this table" click than a raw-text editor.
      const text = [t.headers, ...t.rows].map((r) => r.join("\t")).join("\n");
      navigator.clipboard?.writeText(text);
      showToast("Table copied to clipboard");
    };

    window.__astt_exportMdTable = (tableId) => {
      const t = readTable(tableId);
      if (!t) return;
      // ASTT-7496 (QA #10/#20): this download's own transient anchor click
      // (see downloadCsv) fires OUTSIDE CopilotChatFab's ClickAwayListener
      // subtree and would otherwise close the panel -- see
      // CopilotChatFab.js's clickAwaySuppressorRef/handleClickAway.
      onBeforeDownload?.();
      downloadCsv(`astt-copilot-agent-export-${Date.now()}.csv`, t.headers, t.rows);
      showToast("Exported to CSV");
    };

    return () => {
      delete window.__astt_copyMdTable;
      delete window.__astt_exportMdTable;
    };
  }, [showToast, onBeforeDownload]);

  const handleNewConversation = useCallback(() => {
    sessionIdRef.current = newSessionId();
    clearConversation(STORAGE_NAMESPACE);
    clearReveal();
    setRevealState(null);
    setMessages([]);
    setQuestion("");
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  // ===== Turn navigation rail =====
  // A vertical rail of dots (one per user question) letting the user jump
  // directly to any earlier turn in a long conversation, rather than
  // manually scrolling. messageNodeRefs holds a live DOM ref per message
  // index (populated via the ref callback on each user bubble below) --
  // needed for both the click-to-jump scrollIntoView call and the
  // IntersectionObserver that tracks which turn is currently in view.
  const messageNodeRefs = useRef({});
  const [activeTurnIdx, setActiveTurnIdx] = useState(null);
  const userTurnIndices = messages.reduce((acc, m, idx) => {
    if (m.role === "user") acc.push(idx);
    return acc;
  }, []);

  // ASTT-7496: only the CURRENT/last question in the thread should offer
  // Edit -- editing an earlier one is what made a correct truncation
  // (discarding everything after the edited message) look like the chat
  // had broken, since it could silently wipe a long conversation from an
  // arbitrary point deep in history. Restricting Edit to the last question
  // removes that failure mode at the source, on top of the toast already
  // added in handleEditMessage. Retry doesn't need the same guard -- a
  // retryReason can only ever be set on the trailing message already
  // (sending is blocked by `loading` until the previous attempt resolves
  // or fails), so it's naturally already "last message only" in practice.
  const lastUserIdx = userTurnIndices.length > 0 ? userTurnIndices[userTurnIndices.length - 1] : -1;

  // Scrollspy: observes every user-question node and tracks whichever one
  // is currently nearest the top of the scrollable area, so the rail can
  // highlight "you are here" as the user scrolls, not just respond to
  // clicks. rootMargin shrinks the effective viewport to the top 30% of
  // the scroll container -- without this, several turns fitting on screen
  // at once would all count as "intersecting" simultaneously, giving no
  // single, useful "current position" signal.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root || userTurnIndices.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        const topMost = visible.reduce((a, b) =>
          a.boundingClientRect.top < b.boundingClientRect.top ? a : b
        );
        const idx = Number(topMost.target.dataset.turnIdx);
        if (!Number.isNaN(idx)) setActiveTurnIdx(idx);
      },
      { root, threshold: [0, 0.5, 1], rootMargin: "0px 0px -70% 0px" }
    );

    userTurnIndices.forEach((idx) => {
      const node = messageNodeRefs.current[idx];
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const handleJumpToTurn = (idx) => {
    // ASTT-7496 (QA #17): set the active dot IMMEDIATELY on click rather
    // than waiting entirely on the IntersectionObserver scrollspy to catch
    // up. The observer's rootMargin shrinks the effective "visible" zone to
    // the top 30% of the scroll container (see the effect above), which a
    // turn near the very start/end of a short conversation -- or a short
    // scroll distance -- can end up never satisfying, leaving the clicked
    // dot unhighlighted even though the jump itself worked. The observer
    // still runs and will correct this if the user scrolls further on
    // their own; this just guarantees the dot the user actually clicked
    // lights up right away.
    setActiveTurnIdx(idx);
    messageNodeRefs.current[idx]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Only worth showing once there's an actual list to navigate -- a rail
  // with one or two dots isn't saving anyone a scroll.
  const renderTurnRail = () => {
    if (userTurnIndices.length < 3) return null;
    return (
      <div className={styles.turnRail} role="navigation" aria-label="Jump to a question in this conversation">
        {userTurnIndices.map((idx, i) => {
          const q = messages[idx]?.text ?? "";
          const preview = q.length > 70 ? `${q.slice(0, 70)}\u2026` : q;
          const isActive = idx === activeTurnIdx;
          return (
            <button
              key={idx}
              className={`${styles.turnRailDot} ${isActive ? styles.turnRailDotActive : ""}`}
              onClick={() => handleJumpToTurn(idx)}
              title={preview}
              aria-label={`Jump to question ${i + 1}: ${preview}`}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
    );
  };

  // Clears any in-flight reveal interval without touching revealState --
  // used both by finishReveal (below) and as the first step of starting a
  // NEW reveal, so a reveal still in progress for an older message never
  // keeps ticking in the background once a newer one begins.
  const clearReveal = () => {
    if (revealIntervalRef.current) {
      clearInterval(revealIntervalRef.current);
      revealIntervalRef.current = null;
    }
  };

  // Jumps the currently-revealing message straight to its full text --
  // used when the user clicks the revealing bubble to skip ahead (some
  // people would rather read at their own pace than watch it type out
  // every time), and internally by startReveal to end any PRIOR reveal
  // instantly before beginning a new one.
  const finishReveal = () => {
    clearReveal();
    setRevealState(null);
  };

  // Starts revealing `text` word-by-word for the message at `idx`/
  // `variantIndex`. Re-parses the growing substring through the full
  // renderMarkdown pipeline on every tick (see the render below) rather
  // than revealing raw characters -- revealing raw markdown text would
  // show broken syntax mid-reveal (half a **bold** tag, an incomplete
  // table with no separator row yet, an unclosed [link](...)); re-parsing
  // the whole growing string from scratch each tick means any such
  // artifact self-corrects within the same reveal instead of ever being a
  // separate rendering path to get wrong. Table rows specifically will
  // still show as literal "| cell |" text for a moment until the full
  // separator line has been revealed (markdown.js's table detection needs
  // the whole header+separator before it recognizes a table at all) --
  // an accepted, honest tradeoff of simulating streaming client-side
  // rather than an actual token stream from the agent.
  //
  // Split via /(\s+)/ with a capturing group -- keeps whitespace
  // (including the \n\n block separators renderMarkdown splits blocks on)
  // as its own array elements interleaved with words, so joining revealed
  // elements back with "" reconstructs the EXACT original text, not a
  // whitespace-collapsed approximation that would corrupt block/table
  // parsing.
  //
  // Reveal speed scales with length rather than a fixed per-word delay --
  // a one-line answer should feel snappy, not artificially slow, while a
  // long multi-table answer is capped at ~2.2s total rather than taking
  // proportionally forever.
  const startReveal = (idx, variantIndex, text) => {
    clearReveal();
    const words = (text ?? "").split(/(\s+)/);
    if (words.length === 0 || (words.length === 1 && words[0] === "")) {
      setRevealState(null);
      return;
    }

    const TICK_MS = 40;
    const targetDurationMs = Math.min(350 + words.length * 12, 2200);
    const wordsPerTick = Math.max(1, Math.ceil(words.length / (targetDurationMs / TICK_MS)));

    setRevealState({ idx, variantIndex, words, revealedCount: Math.min(wordsPerTick, words.length) });

    revealIntervalRef.current = setInterval(() => {
      setRevealState((prev) => {
        if (!prev) return prev;
        const next = prev.revealedCount + wordsPerTick;
        if (next >= prev.words.length) {
          clearInterval(revealIntervalRef.current);
          revealIntervalRef.current = null;
          return null; // done -- render falls back to the real, complete message text
        }
        return { ...prev, revealedCount: next };
      });
    }, TICK_MS);
  };

  // Core difference from AgentChatPanel.js: a plain axios POST + await,
  // since Direct Line has no token-level streaming for Copilot Studio
  // agents -- the answer only exists once fully composed, so there's no
  // incremental "progress" event to show, just a single loading state for
  // however long the agent takes (typically a few seconds; the backend's
  // own poll timeout is 60s, see DirectLineAgentService.ChatAsync).
  //
  // skipUserBubble: true for regenerate -- the original user question is
  // already in the thread (this re-sends it without appending a duplicate
  // copy of the same question).
  // ASTT-7496: sessionId at the moment THIS call started, captured once up
  // front. Deliberately NOT re-read from sessionIdRef.current after the
  // await -- if the user clicks "New conversation" (or otherwise moves on)
  // while this request is still in flight, sessionIdRef.current will have
  // already changed by the time this resolves, and this closure needs its
  // OWN original value to correctly recognize that the conversation it was
  // asked in is no longer the current one -- see isStillCurrentSession below.
  const sendMessage = useCallback(
    async (text, { skipUserBubble = false, freshConversation = false } = {}) => {
      const trimmed = (text || question).trim();
      if (!trimmed || loading || !isConnected) return;

      const requestSessionId = sessionIdRef.current;

      if (!skipUserBubble) {
        setMessages((prev) => [...prev, { role: "user", text: trimmed }]);
        setQuestion("");
      }
      setLoading(true);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const response = await axiosInstance.post(
          COPILOT_AGENT_CHAT(),
          { question: trimmed, sessionId: requestSessionId, isRegenerate: freshConversation },
          // Overrides axiosInstance's global 60s default for JUST this
          // call -- not changed instance-wide, since that default is
          // shared by every other API call in the app and 60s is a
          // reasonable ceiling for those. This call specifically needs
          // more room: the backend's own poll timeout is 60s (see
          // DirectLineAgentService.ReplyPollTimeout's remarks on why),
          // so 75s here leaves margin for that to return its own clean
          // 504 rather than this request timing out first with a raw
          // network error.
          { signal: controller.signal, timeout: 75000 }
        );

        const data = response?.data?.data;

        if (!response?.data?.status || !data) {
          throw new Error("Bad response from server");
        }

        const variant = makeVariant(data);

        // ASTT-7496: persist to sessionStorage FIRST, reading the CURRENT
        // stored conversation fresh rather than the closed-over `messages`
        // state -- this is what makes the answer survive the user closing
        // the chat panel while this request was still in flight. Closing
        // the panel fully unmounts CopilotChatPanel (see CopilotChatFab.js's
        // `{open && <CopilotChatPanel .../>}`), which makes every React
        // state update below a silent no-op against a gone component --
        // previously that meant the answer was fully generated (and even
        // logged server-side) but never reached sessionStorage, so
        // reopening the panel showed nothing, and a later Regenerate/retry
        // resent into the same Direct Line conversation Copilot Studio had
        // already answered, producing the "I already told you this, scroll
        // up" reply pointing at nothing the client ever rendered (ASTT-7496
        // root cause #1).
        const stored = loadConversation(STORAGE_NAMESPACE);
        const merge = resolveMergeTarget({
          requestSessionId,
          currentSessionId: sessionIdRef.current,
          stored,
          fallbackMessages: messagesRef.current,
        });
        if (merge.discard) {
          // The user started a new conversation (or otherwise moved on)
          // before this resolved -- there's no valid place left to put a
          // stale answer for an abandoned session. Silently drop it, same
          // as an unmounted failed attempt already does below.
          return;
        }

        const updatedMessages = appendAgentMessage(merge.baseMessages, variant);
        saveConversation(STORAGE_NAMESPACE, requestSessionId, updatedMessages, instanceIdRef.current);

        if (!cancelRef.current) {
          startReveal(merge.baseMessages.length, 0, variant.text);
          setMessages(updatedMessages);
        }
      } catch (err) {
        if (cancelRef.current) return;
        // Keep the user's own question visible and offer an inline retry,
        // rather than silently deleting what they typed and showing a
        // disconnected app-wide toast -- losing the question on failure was
        // the actual complaint here, not just the wording of the message.
        const retryReason = getRetryReason(err);
        setMessages((prev) => {
          const next = [...prev];
          const lastIdx = next.length - 1;
          if (next[lastIdx]?.role === "user") {
            next[lastIdx] = { ...next[lastIdx], retryReason };
          }
          return next;
        });
      } finally {
        abortControllerRef.current = null;
        if (!cancelRef.current) {
          setLoading(false);
        }
      }
    },
    [question, loading, isConnected, dispatch]
  );

  // ASTT-7496: retries a failed question IN PLACE -- clears its retryReason
  // and resends the SAME bubble (skipUserBubble: true) instead of appending
  // a duplicate copy of the question underneath it. Previously this button
  // called sendMessage(msg.text) directly with no options, which pushed a
  // BRAND NEW user bubble on every retry -- after several 502s this left a
  // growing stack of near-identical question bubbles, exactly what QA's
  // screenshot showed (the same question repeated three times, each with
  // its own edit/retry icons, only the last with a rendered response).
  // Also routes through freshConversation (the same flag Regenerate uses)
  // for the same reason Regenerate needs it: if the ORIGINAL attempt
  // actually succeeded server-side (client recorded a timeout/error, but
  // Copilot Studio's own conversation still has the Q&A pair), retrying
  // into the SAME cached conversation would get back a lazy "I already
  // answered this" recap instead of a genuine new attempt (ASTT-7496 root
  // cause #1).
  const handleRetryFailedMessage = async (idx) => {
    const msg = messages[idx];
    if (!msg || msg.role !== "user" || loading || !isConnected) return;

    setMessages((prev) => {
      const next = [...prev];
      if (next[idx]) {
        const { retryReason, ...rest } = next[idx];
        next[idx] = rest;
      }
      return next;
    });

    await sendMessage(msg.text, { skipUserBubble: true, freshConversation: true });
  };

  // Gracefully cancels the in-flight request -- aborts the actual axios
  // call (not just a UI-level "pretend it stopped" flag), so the request is
  // genuinely torn down rather than left running server-side while the UI
  // moves on. Reuses the SAME retryReason/edit-and-retry UI as any other
  // failed attempt (see getRetryReason's ERR_CANCELED case) rather than a
  // separate "stopped" treatment, since both cases boil down to the same
  // thing: this question needs to be retried or edited.
  const handleStop = () => {
    abortControllerRef.current?.abort();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleChip = (prompt) => {
    setQuestion(prompt);
    sendMessage(prompt);
  };

  const handleCopy = (text) => {
    navigator.clipboard?.writeText(text);
    showToast("Copied to clipboard");
  };

  // Regenerate: only ever offered on the LAST agent message (same
  // restriction renderFollowups already uses) -- regenerating a message
  // buried earlier in the thread, with real exchanges after it, is a much
  // more ambiguous operation (what happens to everything that came after?)
  // than this tool needs to solve right now.
  //
  // REWORKED for pagination: previously removed the existing agent message
  // and re-sent the question as a brand-new one via sendMessage -- replaced
  // here with a direct call that APPENDS a new variant onto the SAME
  // message instead, so the prior answer isn't lost and the user can page
  // between them (see renderVariantPager). Also sets isRegenerate: true so
  // DirectLineAgentService.ChatAsync sends this into a fresh, one-off
  // Direct Line conversation rather than the session's ongoing one --
  // without that, Copilot Studio sees the literal identical question
  // already answered moments ago in the same conversation and returns a
  // lazy "I already told you this" recap instead of a genuinely re-derived
  // answer (the exact bug this was built to fix).
  const handleRegenerate = async (idx) => {
    const agentMsg = messages[idx];
    const questionText = messages[idx - 1]?.text;
    if (!questionText || !agentMsg || agentMsg.role !== "agent" || loading || !isConnected) return;

    // ASTT-7496: captured once up front, same reasoning as sendMessage's
    // requestSessionId -- see its remarks.
    const requestSessionId = sessionIdRef.current;
    // Captured now, before the await -- correctly predicts the index the
    // new variant will land at (the current length of this SAME message's
    // variants array), since nothing else can mutate it while `loading`
    // blocks a second concurrent regenerate on the same message.
    const newVariantIndex = agentMsg.variants.length;

    setLoading(true);
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await axiosInstance.post(
        COPILOT_AGENT_CHAT(),
        { question: questionText, sessionId: requestSessionId, isRegenerate: true },
        { signal: controller.signal, timeout: 75000 }
      );

      const data = response?.data?.data;
      if (!response?.data?.status || !data) {
        throw new Error("Bad response from server");
      }

      const variant = makeVariant(data);

      // ASTT-7496: same persist-before-state pattern as sendMessage -- see
      // its remarks for the full rationale. `idx` stays valid against a
      // freshly-reloaded array here because `loading` blocks any other
      // send/regenerate for the whole time this one is in flight, so
      // nothing else can insert/remove messages and shift indices out from
      // under it.
      const stored = loadConversation(STORAGE_NAMESPACE);
      const merge = resolveMergeTarget({
        requestSessionId,
        currentSessionId: sessionIdRef.current,
        stored,
        fallbackMessages: messagesRef.current,
      });
      if (merge.discard) return;

      const updatedMessages = applyRegenerateVariant(merge.baseMessages, idx, variant);
      if (!updatedMessages) return;
      saveConversation(STORAGE_NAMESPACE, requestSessionId, updatedMessages, instanceIdRef.current);

      if (!cancelRef.current) {
        startReveal(idx, newVariantIndex, variant.text);
        setMessages(updatedMessages);
      }
    } catch (err) {
      if (cancelRef.current) return;
      // A failed regenerate attempt shouldn't destroy the still-good
      // existing variant(s) -- surface it as a toast rather than mutating
      // the message, unlike a first-ask failure (which has no prior good
      // answer to protect and so attaches a retryReason to the user bubble
      // instead).
      showToast(getRetryReason(err));
    } finally {
      abortControllerRef.current = null;
      if (!cancelRef.current) {
        setLoading(false);
      }
    }
  };

  // Switches which variant of a multi-variant (regenerated) message is
  // currently displayed -- see renderVariantPager for the prev/next UI this
  // backs.
  const handleVariantChange = (idx, newIndex) => {
    setMessages((prev) => {
      const next = [...prev];
      const target = next[idx];
      if (!target || newIndex < 0 || newIndex >= target.variants.length) return prev;
      next[idx] = { ...target, activeVariantIndex: newIndex };
      return next;
    });
  };

  // Edit and resend: loads the original text back into the composer and
  // discards this message plus everything after it (the standard "edit
  // truncates the branch" behavior) -- the user then sends the edited
  // question as a normal new message. No confirmation prompt, matching how
  // "New conversation" is also a single, immediate, unconfirmed action in
  // this panel already.
  const handleEditMessage = (idx) => {
    const msg = messages[idx];
    if (!msg || msg.role !== "user") return;
    // If the message(s) being discarded include whichever one is currently
    // revealing, stop that reveal too -- otherwise its interval would keep
    // ticking against a message index that no longer points at the same
    // content once the array is sliced.
    if (revealState && revealState.idx >= idx) {
      clearReveal();
      setRevealState(null);
    }
    // ASTT-7496 (QA #19, #25): reproduced -- this truncation is correct,
    // intentional "edit and resend" behavior (the edited question becomes
    // a new turn, so anything already asked/answered after the ORIGINAL
    // version of it no longer has anything to build on). A single-question
    // conversation, editing that one question, legitimately empties the
    // WHOLE thread back to the suggestions screen (slice(0, 0)) -- that's
    // not a bug, it's what made QA's report look like the chat had broken.
    // What was actually missing was any acknowledgment of what happened;
    // this toast closes that gap without adding a confirmation dialog
    // (kept a single, immediate action, same as "New conversation").
    const discardedCount = messages.length - idx - 1;
    setMessages((prev) => prev.slice(0, idx));
    setQuestion(msg.text);
    setTimeout(() => inputRef.current?.focus(), 50);
    if (discardedCount > 0) {
      showToast(
        discardedCount === 1
          ? "Removed 1 message that came after this question"
          : `Removed ${discardedCount} messages that came after this question`
      );
    }
  };

  // Export full conversation as a plain-text transcript -- distinct from
  // the per-table CSV export; this is "save this whole chat", useful for
  // e.g. documenting a support interaction. Skips any message still
  // carrying an unresolved retryReason (a failed attempt with nothing
  // useful to show) rather than including a blank/broken-looking entry.
  const buildConversationTranscript = () => {
    const lines = [
      "ASTT Assistant (Copilot Studio) \u2014 Conversation Export",
      `Exported: ${new Date().toLocaleString()}`,
      "",
    ];
    messages.forEach((msg) => {
      if (msg.role === "user") {
        lines.push("---", "", `You: ${msg.text}`, "");
      } else {
        const activeVariant = msg.variants[msg.activeVariantIndex] ?? msg.variants[0];
        const variantNote =
          msg.variants.length > 1 ? ` (response ${msg.activeVariantIndex + 1} of ${msg.variants.length})` : "";
        lines.push(`Agent${variantNote}: ${activeVariant.text}`, "");
      }
    });
    return lines.join("\n");
  };

  const handleExportConversation = () => {
    if (messages.length === 0) {
      showToast("Nothing to export yet");
      return;
    }
    // ASTT-7496 (QA #10/#20): see the matching comment in the
    // window.__astt_exportMdTable handler above -- same reason.
    onBeforeDownload?.();
    downloadTextFile(`astt-copilot-agent-conversation-${Date.now()}.txt`, buildConversationTranscript());
    showToast("Conversation exported");
  };

  const handleFeedback = async (idx, rating, comment) => {
    const msg = messages[idx];
    const variantIdx = msg?.activeVariantIndex ?? 0;
    const variant = msg?.variants?.[variantIdx];
    if (!variant?.logId) return;

    const previousFeedback = variant.feedback ?? null;
    setMessages((prev) =>
      prev.map((m, i) => {
        if (i !== idx) return m;
        const variants = [...m.variants];
        variants[variantIdx] = { ...variants[variantIdx], feedback: rating };
        return { ...m, variants };
      })
    );

    try {
      await axiosInstance.post(COPILOT_AGENT_FEEDBACK(), {
        logId: variant.logId,
        rating,
        comment: comment || undefined,
      });
      showToast(rating === 1 ? "Thanks for the feedback!" : "Thanks — we'll use this to improve");
      return true;
    } catch {
      setMessages((prev) =>
        prev.map((m, i) => {
          if (i !== idx) return m;
          const variants = [...m.variants];
          variants[variantIdx] = { ...variants[variantIdx], feedback: previousFeedback };
          return { ...m, variants };
        })
      );
      dispatch(
        SnackBarStoreAction.updateSnack({
          open: true,
          message: "Couldn't record feedback for that answer. Please try again.",
          severity: "error",
        })
      );
      return false;
    }
  };

  const handleThumbsDown = async (idx) => {
    await handleFeedback(idx, -1);
    setCommentDraft("");
    setOpenCommentIdx(idx);
  };

  const handleCommentSkip = () => {
    setOpenCommentIdx(null);
    setCommentDraft("");
  };

  const handleCommentSubmit = async (idx) => {
    const trimmed = commentDraft.trim();
    if (!trimmed) {
      handleCommentSkip();
      return;
    }
    setCommentSubmitting(true);
    const ok = await handleFeedback(idx, -1, trimmed);
    setCommentSubmitting(false);
    if (ok) {
      setOpenCommentIdx(null);
      setCommentDraft("");
    }
  };

  // Cycles default -> max -> fullscreen -> default. Clearing any inline
  // width/height from a manual drag-resize before switching tiers, same as
  // the original single-toggle version did -- otherwise a leftover inline
  // style from a drag in "default" mode would fight with the size-tier
  // CSS classes (which use !important specifically to win that fight, but
  // clearing the inline style first is cleaner than relying on it).
  const cycleSize = () => {
    if (panelRef.current) {
      panelRef.current.style.width = "";
      panelRef.current.style.height = "";
    }
    setSizeMode((prev) => (prev === "default" ? "max" : prev === "max" ? "fullscreen" : "default"));
  };

  const handleResizePointerDown = (e) => {
    if (sizeMode !== "default") return;
    resizingRef.current = true;
    const startX = e.clientX;
    const startY = e.clientY;
    const rect = panelRef.current.getBoundingClientRect();
    const startW = rect.width;
    const startH = rect.height;

    const handleMove = (moveEvent) => {
      if (!resizingRef.current || !panelRef.current) return;
      const dx = startX - moveEvent.clientX;
      const dy = startY - moveEvent.clientY;
      // Bounded by the same real docking area the CSS size tiers use (see
      // the docking-bounds effect above) -- previously bounded only by raw
      // window.innerWidth/innerHeight, which is exactly what let a manually
      // dragged panel grow into the sidebar or under the header.
      const maxW = Math.min(900, window.innerWidth - sidebarWidthRef.current - MAX_WIDTH_MARGIN);
      const maxH = Math.min(920, window.innerHeight - headerHeightRef.current - MAX_HEIGHT_MARGIN);
      const newW = Math.max(MIN_WIDTH, Math.min(maxW, startW + dx));
      const newH = Math.max(MIN_HEIGHT, Math.min(maxH, startH + dy));
      panelRef.current.style.width = `${newW}px`;
      panelRef.current.style.height = `${newH}px`;
    };

    const handleUp = () => {
      resizingRef.current = false;
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    e.preventDefault();
  };

  const renderRouterPill = (path) => {
    if (!path) return null;
    const label = path === "documents" ? "Documents" : path === "both" ? "Database + Documents" : "Database";
    const isDoc = path === "documents";
    return (
      <div className={`${styles.router} ${isDoc ? styles.routerDoc : styles.routerDb}`}>
        <span className={styles.routerNode}>Question</span>
        <span className={styles.routerWire} />
        <span className={styles.routerChosen}>
          {isDoc ? <ArticleIcon sx={{ fontSize: 12 }} /> : <StorageIcon sx={{ fontSize: 12 }} />}
          {label}
        </span>
      </div>
    );
  };

  const renderSources = (sources) => {
    if (!sources || sources.length === 0) return null;
    return (
      <div className={styles.sources}>
        <div className={styles.sourcesLabel}>Sources</div>
        {sources.map((s) => {
          const inner = (
            <>
              <ArticleIcon sx={{ fontSize: 14 }} className={styles.sourceIcon} />
              <span className={styles.sourceBody}>
                <span className={styles.sourceTitle}>{s.name}</span>
                {s.lastEdited && <span className={styles.sourceMeta}>Last edited {s.lastEdited}</span>}
              </span>
              {s.url && <OpenInNewIcon sx={{ fontSize: 12 }} className={styles.sourceArrow} />}
            </>
          );
          return s.url ? (
            <a key={s.name} className={styles.sourceCard} href={s.url} target="_blank" rel="noopener noreferrer">
              {inner}
            </a>
          ) : (
            <div key={s.name} className={styles.sourceCard}>
              {inner}
            </div>
          );
        })}
      </div>
    );
  };

  const renderFollowups = (followups, isLastMessage) => {
    if (!followups || followups.length === 0 || !isLastMessage) return null;
    return (
      <div className={styles.followups}>
        <div className={styles.followupsLabel}>Ask next</div>
        {followups.map((q) => (
          <button
            key={q}
            className={styles.followupChip}
            onClick={() => sendMessage(q)}
            disabled={!isConnected}
          >
            <AddIcon sx={{ fontSize: 12 }} className={styles.followupIcon} />
            {q}
          </button>
        ))}
      </div>
    );
  };

  const renderFeedback = (variant, idx, isLastMessage) => {
    return (
      <div className={styles.feedbackRow}>
        <button
          className={styles.feedbackBtn}
          onClick={() => handleCopy(variant.text)}
          title="Copy response"
          aria-label="Copy full response"
        >
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </button>
        {isLastMessage && (
          <button
            className={styles.feedbackBtn}
            onClick={() => handleRegenerate(idx)}
            title="Regenerate response"
            aria-label="Regenerate this response"
            disabled={loading || !isConnected}
          >
            <AutorenewIcon sx={{ fontSize: 14 }} />
          </button>
        )}
        {variant.logId && (
          <>
            <button
              className={`${styles.feedbackBtn} ${variant.feedback === 1 ? styles.feedbackBtnActiveUp : ""}`}
              onClick={() => handleFeedback(idx, 1)}
              title="Helpful"
              aria-label="Mark this answer as helpful"
            >
              {variant.feedback === 1 ? <ThumbUpAltIcon sx={{ fontSize: 14 }} /> : <ThumbUpOffAltIcon sx={{ fontSize: 14 }} />}
            </button>
            <button
              className={`${styles.feedbackBtn} ${variant.feedback === -1 ? styles.feedbackBtnActiveDown : ""}`}
              onClick={() => handleThumbsDown(idx)}
              title="Not helpful"
              aria-label="Mark this answer as not helpful"
            >
              {variant.feedback === -1 ? (
                <ThumbDownAltIcon sx={{ fontSize: 14 }} />
              ) : (
                <ThumbDownOffAltIcon sx={{ fontSize: 14 }} />
              )}
            </button>
          </>
        )}
      </div>
    );
  };

  // Pager shown on any message with more than one variant (i.e. Regenerate
  // has been used on it at least once) -- lets the user flip back and forth
  // to compare the original answer against each regenerated attempt, per
  // the "paginated view to compare" ask. Shown regardless of whether this
  // is still the last message in the thread, since the variants themselves
  // don't go away once a newer question is asked after them.
  const renderVariantPager = (msg, idx) => {
    const total = msg.variants.length;
    const current = msg.activeVariantIndex;
    return (
      <div className={styles.variantPager}>
        <button
          className={styles.variantPagerBtn}
          onClick={() => handleVariantChange(idx, current - 1)}
          disabled={current === 0}
          title="Previous response"
          aria-label="Show previous regenerated response"
        >
          <ChevronLeftIcon sx={{ fontSize: 15 }} />
        </button>
        <span className={styles.variantPagerLabel}>
          Response {current + 1} of {total}
        </span>
        <button
          className={styles.variantPagerBtn}
          onClick={() => handleVariantChange(idx, current + 1)}
          disabled={current === total - 1}
          title="Next response"
          aria-label="Show next regenerated response"
        >
          <ChevronRightIcon sx={{ fontSize: 15 }} />
        </button>
      </div>
    );
  };

  const renderCommentBox = (idx) => {
    if (openCommentIdx !== idx) return null;
    return (
      <div className={styles.commentBox}>
        <div className={styles.commentLabel}>What went wrong? (optional)</div>
        <TextField
          multiline
          minRows={2}
          maxRows={4}
          size="small"
          placeholder="Tell us what wasn't helpful about this answer..."
          value={commentDraft}
          onChange={(e) => setCommentDraft(e.target.value)}
          disabled={commentSubmitting}
          inputProps={{ maxLength: 1000 }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              fontSize: "12px",
              fontFamily: "Urbanist, sans-serif",
              background: "#fff",
            },
          }}
        />
        <div className={styles.commentActions}>
          <button className={styles.commentSkipBtn} onClick={handleCommentSkip} disabled={commentSubmitting}>
            Skip
          </button>
          <button
            className={styles.commentSendBtn}
            onClick={() => handleCommentSubmit(idx)}
            disabled={commentSubmitting || !commentDraft.trim()}
          >
            Send
          </button>
        </div>
      </div>
    );
  };

  const renderStatusDot = () => {
    const meta = {
      checking: { label: "Checking agent status…", className: styles.statusChecking },
      online: { label: "Agent online", className: styles.statusOnline },
      offline: { label: "Agent offline — questions may fail", className: styles.statusOffline },
    }[agentStatus];
    return (
      <span className={styles.statusDotWrap} role="status" aria-label={meta.label} title={meta.label}>
        {agentStatus !== "online" && <span className={`${styles.statusPing} ${meta.className}`} />}
        <span className={`${styles.statusDot} ${meta.className}`} />
      </span>
    );
  };

  const renderConnectionBanner = () => {
    if (isConnected) return null;
    return (
      <div className={styles.connectionBanner}>
        {agentStatus === "offline" ? (
          <>
            <span className={styles.connectionDotOffline} />
            ASTT Assistant (Copilot Studio) is unavailable right now — retrying automatically…
          </>
        ) : (
          <>
            <span className={styles.connectionSpinner} />
            Connecting to ASTT Assistant (Copilot Studio)…
          </>
        )}
      </div>
    );
  };

  return (
    <div
      className={`${styles.panel} ${sizeMode === "max" ? styles.panelMax : ""} ${
        sizeMode === "fullscreen" ? styles.panelFullscreen : ""
      }`}
      ref={panelRef}
    >
      {sizeMode === "default" && (
        <div className={styles.resizeHandle} onPointerDown={handleResizePointerDown} title="Drag to resize">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M4 20L20 4M10 20L20 10M16 20L20 16" />
          </svg>
        </div>
      )}

      <div className={styles.header}>
        <span className={styles.headerTitle}>ASTT Assistant</span>
        <span className={styles.headerTag}>Copilot Studio</span>
        {renderStatusDot()}
        <div className={styles.headerActions}>
          <button className={styles.iconBtn} onClick={handleExportConversation} title="Export conversation">
            <FileDownloadIcon sx={{ fontSize: 16 }} />
          </button>
          <button
            className={styles.iconBtn}
            onClick={cycleSize}
            title={sizeMode === "default" ? "Maximize" : sizeMode === "max" ? "Full screen" : "Restore"}
          >
            {sizeMode === "fullscreen" ? (
              <CloseFullscreenIcon sx={{ fontSize: 15 }} />
            ) : (
              <OpenInFullIcon sx={{ fontSize: 15 }} />
            )}
          </button>
          <button className={styles.iconBtn} onClick={handleNewConversation} title="New conversation">
            <RefreshIcon sx={{ fontSize: 17 }} />
          </button>
          <button className={styles.iconBtn} onClick={onClose} title="Close">
            <CloseIcon sx={{ fontSize: 17 }} />
          </button>
        </div>
      </div>

      <div className={styles.bodyWrap}>
        <div
          className={`${styles.body} ${userTurnIndices.length >= 3 ? styles.bodyWithRail : ""}`}
          ref={scrollRef}
        >
          {renderConnectionBanner()}

        {messages.length === 0 && (
          <div className={styles.emptyState}>
            <SmartToyOutlinedIcon className={styles.emptyIcon} sx={{ fontSize: 34 }} />
            <p className={styles.emptyTitle}>Ask about a trainee, a roster, or ASTT documentation</p>
            <p className={styles.emptyIntro}>
              I can look up trainees, rosters, and class requests, check equipment/Taleo/
              CenterPoint/OMT status, and run quick counts or filters — or answer how-to
              questions from ASTT's documentation.
            </p>

            <div className={styles.quickGroup}>
              <div className={styles.quickGroupLabel}>
                <StorageIcon sx={{ fontSize: 11 }} /> Database
              </div>
              {suggestions.database.map((q) => (
                <button key={q} className={styles.chip} onClick={() => handleChip(q)} disabled={!isConnected}>
                  {q}
                </button>
              ))}
            </div>

            <div className={styles.quickGroup}>
              <div className={`${styles.quickGroupLabel} ${styles.quickGroupLabelDoc}`}>
                <ArticleIcon sx={{ fontSize: 11 }} /> Documents
              </div>
              {suggestions.documents.map((q) => (
                <button
                  key={q}
                  className={`${styles.chip} ${styles.chipDoc}`}
                  onClick={() => handleChip(q)}
                  disabled={!isConnected}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, idx) =>
          msg.role === "user" ? (
            <div
              key={idx}
              className={styles.userBubbleWrap}
              ref={(el) => {
                messageNodeRefs.current[idx] = el;
              }}
              data-turn-idx={idx}
            >
              <div className={styles.userBubble}>{msg.text}</div>
              {msg.retryReason && (
                // ASTT-7496: previously the ONLY user-visible indication of
                // a failure was a hover tooltip on the small retry icon
                // below (title={msg.retryReason}) -- easy to miss entirely,
                // and unusable on touch devices where hover tooltips don't
                // fire at all. .retryReasonText already existed in this
                // file's own CSS module but was never actually rendered
                // anywhere -- this wires it up so the reason (e.g. "The
                // agent took longer than expected to respond") is always
                // visible, not just discoverable on hover.
                <div className={styles.retryReasonText}>{msg.retryReason}</div>
              )}
              <div className={styles.userActionsRow}>
                {idx === lastUserIdx && (
                  <button
                    className={styles.userActionBtn}
                    onClick={() => handleEditMessage(idx)}
                    title="Edit and resend"
                    aria-label="Edit and resend this question"
                    disabled={loading}
                  >
                    <EditIcon sx={{ fontSize: 13 }} />
                  </button>
                )}
                {msg.retryReason && (
                  <button
                    className={`${styles.userActionBtn} ${styles.userRetryBtn}`}
                    onClick={() => handleRetryFailedMessage(idx)}
                    title={msg.retryReason}
                    aria-label={`Try again — ${msg.retryReason}`}
                    disabled={loading}
                  >
                    <ReplayIcon sx={{ fontSize: 13 }} />
                  </button>
                )}
              </div>
            </div>
          ) : (
            (() => {
              const activeVariant = msg.variants[msg.activeVariantIndex] ?? msg.variants[0];
              const isRevealing =
                revealState?.idx === idx && revealState.variantIndex === msg.activeVariantIndex;
              const displayText = isRevealing
                ? revealState.words.slice(0, revealState.revealedCount).join("")
                : activeVariant.text;
              return (
                <div key={idx} className={styles.replyGroup}>
                  {renderRouterPill(activeVariant.path)}
                  <div className={styles.agentBubbleWrap}>
                    <div
                      className={styles.agentBubble}
                      onClick={isRevealing ? finishReveal : undefined}
                      title={isRevealing ? "Click to show the full response" : undefined}
                      style={isRevealing ? { cursor: "pointer" } : undefined}
                      dangerouslySetInnerHTML={{
                        __html:
                          renderMarkdown(displayText) +
                          (isRevealing ? '<span class="' + styles.typingCursor + '">\u258c</span>' : ""),
                      }}
                    />
                  </div>
                  {!isRevealing && msg.variants.length > 1 && renderVariantPager(msg, idx)}
                  {!isRevealing && formatDuration(activeVariant.durationMs) && (
                    <div className={styles.durationLabel} title="Time to generate this response">
                      {formatDuration(activeVariant.durationMs)}
                    </div>
                  )}
                  {!isRevealing && renderSources(activeVariant.sources)}
                  {!isRevealing && renderFeedback(activeVariant, idx, idx === messages.length - 1)}
                  {!isRevealing && renderCommentBox(idx)}
                  {!isRevealing && renderFollowups(activeVariant.followups, idx === messages.length - 1)}
                </div>
              );
            })()
          )
        )}

        {loading && (
          <div className={styles.loadingRow}>
            <div className={styles.dot} />
            <div className={styles.dot} />
            <div className={styles.dot} />
            <span className={styles.progressLabel}>{progressLabel}</span>
          </div>
        )}
        </div>
        {renderTurnRail()}
      </div>

      <div className={styles.inputArea}>
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder={isConnected ? "Ask the ASTT Assistant..." : "Waiting for the agent to connect…"}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading || !isConnected}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              fontSize: "13px",
              fontFamily: "Urbanist, sans-serif",
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#3299ff" },
            },
          }}
        />
        {loading ? (
          <button className={styles.stopBtn} onClick={handleStop} title="Stop" aria-label="Stop generating">
            <StopRoundedIcon sx={{ fontSize: 18 }} />
          </button>
        ) : (
          <button
            className={styles.sendBtn}
            onClick={() => sendMessage()}
            disabled={!question.trim() || !isConnected}
            title="Send"
          >
            <ArrowUpwardIcon sx={{ fontSize: 18 }} />
          </button>
        )}
      </div>
      <div className={styles.composerFoot}>Read-only · answers may need verification · Copilot Studio (evaluation)</div>

      {toastMessage && <div className={styles.toast}>{toastMessage}</div>}
    </div>
  );
}
